import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import { Skill, SkillFile } from "../skills/types";
import {
  CopySkillTemplateResponse,
  GetSkillTemplateFileResponse,
  ListSkillTemplateFilesResponse,
  SkillTemplate,
  SkillTemplateResponse,
  SkillTemplatesResponse,
} from "./types";

export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/skill-templates",
  SHOW: "/api/v2/org/:organization_id/user/:user_id/skill-templates/:template_id",
  /** GET list; GET single file uses the same prefix + encoded path (server: `.../files/*file_path`). */
  LIST_FILES:
    "/api/v2/org/:organization_id/user/:user_id/skill-templates/:template_id/files",
  COPY: "/api/v2/org/:organization_id/user/:user_id/skill-templates/:template_id/copy",
  DELETE:
    "/api/v2/org/:organization_id/user/:user_id/skill-templates/:template_id",
};

/** Encode path segments for the catch-all `*file_path` segment after `.../files/`. */
function encodeTemplateFilePathSuffix(filePath: string): string {
  return filePath
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function buildTemplateFileUrl(
  listFilesBaseUrl: string,
  filePath: string,
): string {
  const suffix = encodeTemplateFilePathSuffix(filePath);
  return suffix ? `${listFilesBaseUrl}/${suffix}` : listFilesBaseUrl;
}

export class SkillTemplatesAPI {
  constructor(private client: CohesiveAPIClient) {}

  /** Lists the catalog of templates visible to the user (system + their org). */
  async list(config?: AxiosRequestConfig): Promise<SkillTemplate[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<SkillTemplatesResponse>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
      }),
      config,
    );
    return response.data.data.skill_templates;
  }

  async getById(
    templateId: string,
    config?: AxiosRequestConfig,
  ): Promise<SkillTemplate> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<SkillTemplateResponse>(
      route(ENDPOINTS.SHOW, {
        organization_id: organizationId,
        user_id: userId,
        template_id: templateId,
      }),
      config,
    );
    return response.data.data.skill_template;
  }

  async listFiles(
    templateId: string,
    config?: AxiosRequestConfig,
  ): Promise<SkillFile[]> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response =
      await this.client.axios.get<ListSkillTemplateFilesResponse>(
        route(ENDPOINTS.LIST_FILES, {
          organization_id: organizationId,
          user_id: userId,
          template_id: templateId,
        }),
        config,
      );
    return response.data.data.files;
  }

  async getFile(
    templateId: string,
    filePath: string,
    config?: AxiosRequestConfig,
  ): Promise<SkillFile> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const base = route(ENDPOINTS.LIST_FILES, {
      organization_id: organizationId,
      user_id: userId,
      template_id: templateId,
    });
    const response = await this.client.axios.get<GetSkillTemplateFileResponse>(
      buildTemplateFileUrl(base, filePath),
      config,
    );
    return response.data.data.file;
  }

  /** Copies the template into a new private skill (Manual default). Returns the new skill. */
  async copy(templateId: string, config?: AxiosRequestConfig): Promise<Skill> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<CopySkillTemplateResponse>(
      route(ENDPOINTS.COPY, {
        organization_id: organizationId,
        user_id: userId,
        template_id: templateId,
      }),
      {},
      config,
    );
    return response.data.data.skill;
  }

  /** Unpublishes (hard-deletes) an organization template. Publisher or org admin only. */
  async unpublish(
    templateId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        template_id: templateId,
      }),
      config,
    );
  }
}
